import React, { useState, useEffect, useRef, useCallback } from 'react';
import { normalizeImageUrl } from '../utils/urlSanitizer';
import axios from 'axios';
import { getAxiosConfig } from '../utils/http';
import { loadScript } from '@paypal/paypal-js';
import { loadStripe } from '@stripe/stripe-js';
// Removed react-paypal-button-v2 usage; rendering buttons directly via SDK
// import { PayPalButton } from 'react-paypal-button-v2';
import { Link, useHistory } from 'react-router-dom';
import { Modal, Form, Button, Row, Col, ListGroup, Image, Card, Dropdown, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Message from '../components/Message';
import Loader from '../components/Loader';
import StripePayForm from '../components/StripePayForm';
import AdminDecisionModal from '../components/AdminDecisionModal';
import StepUpPasswordModal from '../components/StepUpPasswordModal';
import { requestStepUpToken } from '../utils/stepUp';
import { sendTrackingNumbers, sendShipmentTracking } from '../slices/trackingSlice';
import {
  getOrderDetails,
  payOrder,
  payOrderStripe,
  deliverOrder,
  markOrderProcessed,
  decideCancelRequest,
  approveRefundRequest,
  denyRefundRequest,
  initiateRefund,
  adminCancelOrder,
  resetOrderFlags,
} from '../slices/orderSlice';
import { setUserInfo } from '../slices/userSlice';
import Meta from '../components/Meta';

// Currency formatter (consistent 2-decimal USD). Future usage: formatMoney(order.taxPrice), formatMoney(order.shippingPrice)
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const formatMoney = (v) => currencyFormatter.format(Number.isFinite(+v) ? +v : 0);

export const formatRefundSubmittedAt = (value) => {
  if (!value) return 'Timestamp unavailable';
  const submittedAt = new Date(value);
  return Number.isNaN(submittedAt.getTime())
    ? 'Timestamp unavailable'
    : submittedAt.toLocaleString();
};

const buildAddressLine = (shippingAddress) => {
  const parts = [
    String(shippingAddress?.address1 || '').trim(),
    String(shippingAddress?.address2 || '').trim(),
    String(shippingAddress?.city || '').trim(),
    String(shippingAddress?.state || '').trim(),
    String(shippingAddress?.postalCode || '').trim(),
    String(shippingAddress?.country || '').trim(),
  ].filter(Boolean);
  return parts.join(', ');
};

const OrderScreen = ({ match }) => {
  const orderId = match.params.id;
  const dispatch = useDispatch();
  const history = useHistory();

  const orderState = useSelector((state) => state.order);
  const order = orderState.orderDetails;
  const {
    loading,
    error,
    successPay,
    successDeliver,
    successShip,
    successProcess,
    successCancelRequest,
    successCancelDecision,
    successRefundRequest,
    successRefundInitiate,
    loadingPay,
    loadingDeliver,
    loadingShip,
    loadingProcess,
    loadingCancelDecision,
    loadingRefundInitiate,
  } = orderState;

  const { loading: loadingTracking, error: errorTracking, trackingHistory, trackingStatus } =
    useSelector((state) => state.tracking);

  // New: get logged-in user info to gate admin-only controls
  const userInfo = useSelector((state) => state.user?.userInfo || state.userLogin?.userInfo);
  const isAdmin = !!userInfo?.isAdmin;

  // Guest path: local order cache when unauthenticated
  const [orderLocal, setOrderLocal] = useState(null);
  const accessKey = `orderAccessToken:${orderId}`;
  const guestFetchCacheRef = useRef({ key: '', inFlight: false });
  const guestSessionHydratedRef = useRef(false);

  // Use redux order when authenticated, otherwise local guest-loaded order
  const baseOrder = order || orderLocal;
  // Treat orders as paid if either flag is set.
  // Some legacy/edge cases may have `paidAt` populated while `isPaid` is false.
  const orderIsPaid = Boolean(baseOrder?.isPaid || baseOrder?.paidAt);
  // Derive order contact info primarily from order, not user
  const orderFirstName = baseOrder?.firstName || baseOrder?.shipToFirstName || baseOrder?.user?.firstName || '';
  const orderLastName = baseOrder?.lastName || baseOrder?.shipToLastName || baseOrder?.user?.lastName || '';
  const orderEmail = baseOrder?.email || baseOrder?.shippingAddress?.email || baseOrder?.user?.email || '';

  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState(null);
  const [sdkAttempts, setSdkAttempts] = useState(0);
  const MAX_SDK_ATTEMPTS = 3;
  const [paypalRenderNonce, setPaypalRenderNonce] = useState(0);

  // Local-only payment selection (UI state only)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('PayPal');
  const paymentMethodTouchedRef = useRef(false);

  // Stripe
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeKeyError, setStripeKeyError] = useState('');
  const stripeReturnHandledRef = useRef(false);
  const [showModal, setShowModal] = useState(false);
  const [showShipmentModal, setShowShipmentModal] = useState(false);
  const [res, setRes] = useState([]);
  const [trackingNumbers, setTrackingNumbers] = useState([]);
  const [bigNumber, setBigNumber] = useState([]);
  // Guest access verify UI state
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState('');
  const [verifyZip, setVerifyZip] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [loginRequiredOpen, setLoginRequiredOpen] = useState(false);
  const [loginRequiredMsg, setLoginRequiredMsg] = useState('');
  // Local loading for guest fetches so we don't rely on Redux flags
  const [guestLoading, setGuestLoading] = useState(false);

  // Non-admin help messages
  const [helpMessage, setHelpMessage] = useState('');
  const [helpMessageVariant, setHelpMessageVariant] = useState('info');
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [showApproveRefundModal, setShowApproveRefundModal] = useState(false);
  const [showDenyRefundModal, setShowDenyRefundModal] = useState(false);
  const [loadingRefundDecision, setLoadingRefundDecision] = useState(false);
  const [showAdminCancelModal, setShowAdminCancelModal] = useState(false);
  const [showAdminDeliverModal, setShowAdminDeliverModal] = useState(false);
  const [showAdminRefundModal, setShowAdminRefundModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  // Only show pay actions once the correct order is fully loaded.
  // This avoids a brief flash of the floating CTA while order details are still loading.
  const orderMatchesRoute = Boolean(baseOrder?._id) && String(baseOrder._id) === String(orderId);
  const orderLoaded = orderMatchesRoute && !loading && !guestLoading;
  const showPayNowFab = orderLoaded && !orderIsPaid;

  const getVariantLabel = useCallback((item) => {
    if (!item) return '';

    const vv = Array.isArray(item.variantValues) ? item.variantValues.filter(Boolean) : [];
    if (vv.length) {
      const parts = vv
        .map((x) => {
          const prop = String(x?.propName || x?.propId || '').trim();
          const val = String(x?.valueName || x?.valueId || '').trim();
          if (prop && val) return `${prop}: ${val}`;
          if (val) return val;
          return '';
        })
        .filter(Boolean);
      if (parts.length) return parts.join(', ');
    }

    const legacy = String(item.selectedColorsVariants || '').trim();
    if (legacy) return legacy;

    const sig = String(item.variantSignature || '').trim();
    if (sig) return `Variant: ${sig}`;

    return '';
  }, []);

  // Helper: attempt to hydrate user session from server (handles guest cookie)
  const ensureGuestSession = useCallback(async () => {
    try {
      if (userInfo) return; // already have a user
      const state = { user: { userInfo } };
      const config = getAxiosConfig(state, { withCredentials: true, forbidAuthForGuest: true });
      const { data } = await axios.get('/api/users/session', config);
      if (data && data._id) {
        dispatch(setUserInfo(data));
        try { localStorage.setItem('userInfo', JSON.stringify(data)); } catch {}
      }
    } catch {
      // ignore: not logged or no guest cookie yet
    }
  }, [dispatch, userInfo]);

  // Try loading under these namespaces to avoid collisions; fallback to default 'paypal'
  const PAYPAL_NAMESPACES = ['paypal_xsdk', 'paypal'];
  const currentNsRef = useRef(PAYPAL_NAMESPACES[0]);

  // Ref for rendering PayPal Buttons directly
  const paypalRef = useRef(null);

  const paymentSectionRef = useRef(null);

  const scrollToPayment = useCallback(() => {
    if (!paymentSectionRef.current) return;
    try {
      paymentSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch {
      // Fallback for environments without smooth scrolling support
      paymentSectionRef.current.scrollIntoView();
    }
  }, []);

  // Initialize payment selection from order when order loads (do not override manual selection)
  useEffect(() => {
    if (!baseOrder || orderIsPaid) return;
    if (paymentMethodTouchedRef.current) return;
    setSelectedPaymentMethod(baseOrder?.paymentMethod || 'PayPal');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately depends on baseOrder's stable fields, not the object reference (matches pattern used elsewhere in this file)
  }, [baseOrder?._id, orderIsPaid, baseOrder?.paymentMethod]);

  // Helper: verify PayPal SDK API surface is available before rendering buttons
  const isPayPalReady = () => {
    try {
      const ns = typeof window !== 'undefined' ? window[currentNsRef.current] : undefined;
      return !!ns && !!ns.Buttons && typeof ns.Buttons === 'function';
    } catch (e) {
      return false;
    }
  };

  // Compute Items subtotal directly from orderItems to ensure accuracy
  const itemsSubtotal = React.useMemo(() => {
    const items = baseOrder?.orderItems || [];
    const sumCents = items.reduce((acc, item) => {
      const price = Number(item?.price) || 0;
      const q = Number(item?.qty) || 0;
      return acc + Math.round(price * 100) * q;
    }, 0);
    return (sumCents / 100).toFixed(2); // string ok for formatMoney
  }, [baseOrder?.orderItems]);

  // Build order item options for tracking item dropdowns
  const orderItemOptions = React.useMemo(
    () =>
      (baseOrder?.orderItems || []).map((it, i) => ({
        value: String(i),
        label: `${it.name}${getVariantLabel(it) ? ` (${getVariantLabel(it)})` : ''} x${it.qty}`,
        name: it.name,
        variant: getVariantLabel(it),
        qty: Number(it.qty) || 0,
      })),
    [baseOrder?.orderItems, getVariantLabel]
  );

  // Helpers: allocation and remaining quantities across draft shipments
  const computeAllocatedByIndex = React.useCallback((draft) => {
    const acc = {};
    (draft || []).forEach((ship) => {
      (ship?.items || []).forEach((it) => {
        const idx = String(it?.orderItemIndex || '');
        const q = Number(it?.qty) || 0;
        if (!idx) return;
        acc[idx] = (acc[idx] || 0) + q;
      });
    });
    return acc;
  }, []);

  const remainingByIndex = React.useMemo(() => {
    const allocated = computeAllocatedByIndex(bigNumber);
    const remaining = {};
    (baseOrder?.orderItems || []).forEach((it, i) => {
      const total = Number(it?.qty) || 0;
      const used = Number(allocated[String(i)] || 0);
      remaining[String(i)] = Math.max(0, total - used);
    });
    return remaining;
  }, [baseOrder?.orderItems, bigNumber, computeAllocatedByIndex]);

  const optionWithRemaining = (value) => {
    const opt = orderItemOptions.find((o) => o.value === value);
    if (!opt) return null;
    const rem = Number(remainingByIndex[String(value)] || 0);
    const total = Number(opt.qty || 0);
    return { ...opt, remaining: rem, total };
  };

  // Transform server trackingNumbers to editable draft
  const toDraftFromExisting = React.useCallback((existing, items) => {
    const list = Array.isArray(existing) ? existing : [];
    const hasNew = list.some((t) => Array.isArray(t?.items));
    if (hasNew) {
      return list.map((t) => ({
        number: t?.number || '',
        items: (Array.isArray(t?.items) ? t.items : []).map((x) => ({
          orderItemIndex: String(x?.orderItemIndex ?? ''),
          qty: Number(x?.qty) > 0 ? Number(x.qty) : 1,
        })),
      }));
    }
    // Legacy path: trackingItem is array of strings like "Item label xOrderQty ×allocQty"
    const labels = (items || []).map((it, i) => ({
      value: String(i),
      label: `${it.name}${getVariantLabel(it) ? ` (${getVariantLabel(it)})` : ''} x${it.qty}`,
    }));
    const labelToValue = new Map(labels.map((l) => [l.label, l.value]));
    const parseLegacy = (s) => {
      const str = String(s || '').trim();
      // Capture base label and optional trailing multiplication qty (× used in legacy)
      const m = str.match(/^(.*?)(?:\s*×\s*(\d+))?$/);
      const base = (m && m[1] ? m[1] : str).trim();
      const qty = m && m[2] ? Math.max(1, parseInt(m[2], 10) || 1) : 1;
      return { base, qty };
    };
    return list.map((t) => {
      const legacy = Array.isArray(t?.trackingItem) ? t.trackingItem : [];
      const itemsParsed = [];
      legacy.forEach((lab) => {
        const { base, qty } = parseLegacy(lab);
        const val = labelToValue.get(base) || '';
        if (val !== '') itemsParsed.push({ orderItemIndex: String(val), qty });
      });
      // Optionally merge duplicates of same orderItemIndex
      const merged = [];
      const acc = new Map();
      itemsParsed.forEach((it) => {
        const key = String(it.orderItemIndex);
        acc.set(key, (acc.get(key) || 0) + (Number(it.qty) || 1));
      });
      acc.forEach((q, k) => merged.push({ orderItemIndex: String(k), qty: Number(q) }));
      return { number: t?.number || '', items: merged };
    });
  }, [getVariantLabel]);

  // Fetch order details (authenticated) or guest path with token fallback
  const fetchOrder = useCallback(async ({ force = false } = {}) => {
    try {
      if (guestFetchCacheRef.current.inFlight) return;
      setGuestLoading(true);
      const token = sessionStorage.getItem(accessKey);
      const cacheKey = `${orderId}|${token || ''}`;
      if (!force && orderLocal?._id && String(orderLocal._id) === String(orderId) && guestFetchCacheRef.current.key === cacheKey) {
        return;
      }
      guestFetchCacheRef.current.inFlight = true;
      const url = token
        ? `/api/orders/${orderId}/guest?accessToken=${encodeURIComponent(token)}`
        : `/api/orders/${orderId}`;
      const state = { user: { userInfo } };
      const config = getAxiosConfig(state, { withCredentials: true, forbidAuthForGuest: true });
      const { data } = await axios.get(url, config);
      setOrderLocal(data);
      guestFetchCacheRef.current.key = cacheKey;
      setVerifyOpen(false);
      setVerifyError('');
      setLoginRequiredOpen(false);
      setLoginRequiredMsg('');
      // If we're using a one-off access token and the UI has no user, set a minimal guest user so UI behaves consistently
      if (token && !userInfo) {
        const guestUi = { username: 'guest', firstName: 'GUEST', lastName: 'GUEST', isGuest: true };
        dispatch(setUserInfo(guestUi));
        try { localStorage.setItem('userInfo', JSON.stringify(guestUi)); } catch {}
      }
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      const msg = err?.response?.data?.message || err?.response?.data?.msg || err?.message;
      if (code === 'LOGIN_REQUIRED') {
        try { sessionStorage.removeItem(accessKey); } catch {}
        setOrderLocal(null);
        setVerifyOpen(false);
        setVerifyError('');
        setLoginRequiredMsg(String(msg || 'Please log in to view this order.'));
        setLoginRequiredOpen(true);
        return;
      }
      if (status === 401 || status === 403) {
        setVerifyOpen(true);
      } else {
        setVerifyError('Failed to load order');
      }
    } finally {
      guestFetchCacheRef.current.inFlight = false;
      setGuestLoading(false);
    }
  }, [orderId, accessKey, orderLocal?._id, userInfo, dispatch]);

  const authKey = userInfo?.isAdmin ? 'admin' : userInfo?._id ? `user:${userInfo._id}` : userInfo?.isGuest ? 'guest-ui' : 'anon';

  useEffect(() => {
    if (userInfo && (userInfo.isAdmin || userInfo._id)) {
      setOrderLocal(null);
      dispatch(getOrderDetails(orderId));
    } else {
      fetchOrder();
      // Try to hydrate a guest session if cookie exists (once per mount)
      if (!guestSessionHydratedRef.current) {
        guestSessionHydratedRef.current = true;
        ensureGuestSession();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately depends on authKey (derived, stable identity) rather than userInfo directly
  }, [dispatch, orderId, authKey, fetchOrder, ensureGuestSession]);

  // Helper: remove any existing PayPal SDK and global namespace
  const removePayPalSdk = () => {
    try {
      const scripts = document.querySelectorAll('script[src*="paypal.com/sdk/js"]');
      scripts.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
      if (typeof window !== 'undefined') {
        for (const ns of PAYPAL_NAMESPACES) {
          try { delete window[ns]; } catch (e) {}
          // @ts-ignore
          window[ns] = undefined;
        }
        try { delete window.paypal; } catch (e) {}
        // @ts-ignore
        window.paypal = undefined;
      }
    } catch (e) {}
  };

  // Attempt to load PayPal SDK with limited automatic retries
  const attemptLoadPayPalSdk = async (attempt = 1) => {
    setSdkError(null);
    setSdkAttempts(attempt);
    try {
      const existing = document.querySelector('script[src*="paypal.com/sdk/js"]');
      if (existing && typeof window !== 'undefined' && (window[currentNsRef.current] || window.paypal)) {
        if (isPayPalReady()) {
          setSdkReady(true);
          return;
        }
        existing.parentNode && existing.parentNode.removeChild(existing);
      }

  const state = { user: { userInfo } };
  const config = getAxiosConfig(state, { withCredentials: true, forbidAuthForGuest: true });
  const { data } = await axios.get('/api/config/paypal', config);
    const rawClientId = typeof data === 'string' ? data : data?.clientId || data?.clientID || data?.id;
    const clientId = typeof rawClientId === 'string'
      ? rawClientId.trim().replace(/^['"](.+)['"]$/, '$1').trim()
      : '';
      if (!clientId) throw new Error('Missing PayPal Client ID');

      // Rotate namespace between attempts to avoid cached bad state
      const nsIndex = (attempt - 1) % PAYPAL_NAMESPACES.length;
      const ns = PAYPAL_NAMESPACES[nsIndex];
      currentNsRef.current = ns;

      // Use official loader to avoid timing issues
      await loadScript({
        'client-id': clientId,
        currency: 'USD',
        components: 'buttons',
        intent: 'capture',
        'data-namespace': ns,
      });

      // Poll for readiness after loader resolves (namespace attachment can lag)
      const start = Date.now();
      await new Promise((resolve, reject) => {
        const check = () => {
          if (isPayPalReady()) return resolve(true);
          if (Date.now() - start > 15000) return reject(new Error('paypal Buttons not ready'));
          setTimeout(check, 100);
        };
        check();
      });

      setSdkReady(true);
    } catch (err) {
      if (attempt < MAX_SDK_ATTEMPTS) {
        setTimeout(() => {
          removePayPalSdk();
          attemptLoadPayPalSdk(attempt + 1);
        }, attempt * 1000);
      } else {
        setSdkError('Failed to initialize PayPal SDK');
      }
    }
  };

  const handleReloadPayPal = () => {
    setSdkError(null);
    setSdkAttempts(0);

    // Clear and force a re-render of buttons without tearing down the SDK.
    // Removing/reloading the PayPal SDK can trigger opaque cross-origin "Script error." events in dev.
    try {
      if (paypalRef.current) paypalRef.current.innerHTML = '';
    } catch {
      // ignore
    }

    setPaypalRenderNonce((n) => n + 1);

    // Ensure SDK is loaded (but avoid removing existing scripts/namespaces).
    if (isPayPalReady()) {
      setSdkReady(true);
      return;
    }

    setSdkReady(false);
    attemptLoadPayPalSdk(1);
  };

  // Load PayPal SDK when needed
  useEffect(() => {
    if (baseOrder && !orderIsPaid && selectedPaymentMethod === 'PayPal') {
      if (!isPayPalReady()) {
        attemptLoadPayPalSdk(1);
      } else {
        setSdkReady(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseOrder?._id, orderIsPaid, selectedPaymentMethod]);

  // Load Stripe publishable key when needed
  useEffect(() => {
    let mounted = true;

    const loadStripeKey = async () => {
      setStripeKeyError('');
      try {
        const state = { user: { userInfo } };
        const config = getAxiosConfig(state, { withCredentials: true, forbidAuthForGuest: true });
        const { data } = await axios.get('/api/config/stripe', config);
        const key = typeof data === 'string' ? data : data?.publishableKey || data?.key;
        if (!key) throw new Error('Missing Stripe publishable key');
        if (mounted) setStripePromise(loadStripe(key));
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Failed to initialize Stripe';
        if (mounted) setStripeKeyError(String(msg));
      }
    };

    if (baseOrder && !orderIsPaid && selectedPaymentMethod === 'Stripe') {
      loadStripeKey();
    } else {
      setStripePromise(null);
      setStripeKeyError('');
    }

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately excludes 'baseOrder' object reference (matches pattern used elsewhere in this file); only whether it's present/paid/method matters here
  }, [orderIsPaid, selectedPaymentMethod, userInfo]);

  // Stripe Embedded Checkout return: /order/:id?session_id=...
  useEffect(() => {
    try {
      if (!baseOrder || orderIsPaid) return;
      if (typeof window === 'undefined') return;

      const params = new URLSearchParams(window.location.search || '');
      const sessionId = params.get('session_id') || params.get('sessionId');
      if (!sessionId) return;
      if (stripeReturnHandledRef.current) return;
      stripeReturnHandledRef.current = true;

      dispatch(payOrderStripe({ orderId, paymentResult: { session_id: sessionId } }));
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately excludes 'baseOrder' object reference (matches pattern used elsewhere in this file); only its presence/paid-status matters here
  }, [dispatch, orderId, orderIsPaid]);

  // If a cross-origin PayPal "Script error." was suppressed at startup,
  // surface a recoverable message here.
  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const msg = window.__LOTUSTOR_PAYPAL_SDK_ERROR__;
      if (msg && !sdkError) {
        setSdkError(String(msg));
      }
      // Clear after reading so it doesn't persist across navigations
      window.__LOTUSTOR_PAYPAL_SDK_ERROR__ = '';
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the global guard captures an opaque script error after mount (e.g. during initial SDK load),
  // surface it here so the user sees a recoverable message instead of a blank payment section.
  useEffect(() => {
    try {
      if (!baseOrder || orderIsPaid) return;
      if (selectedPaymentMethod !== 'PayPal') return;
      if (sdkReady) return;
      if (typeof window === 'undefined') return;

      const msg = window.__LOTUSTOR_PAYPAL_SDK_ERROR__;
      if (msg) {
        setSdkError(String(msg));
        window.__LOTUSTOR_PAYPAL_SDK_ERROR__ = '';
      }
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately depends on baseOrder's stable id, not the object reference (matches pattern used elsewhere in this file)
  }, [baseOrder?._id, orderIsPaid, selectedPaymentMethod, sdkReady]);

  // Guard: PayPal SDK is cross-origin and can throw opaque "Script error." events.
  // Prevent CRA runtime overlay from taking over; show a recoverable message instead.
  useEffect(() => {
    if (!baseOrder || orderIsPaid) return;
    if (typeof window === 'undefined') return;

    const onErrorCapture = (event) => {
      try {
        const msg = String(event?.message || '');
        if (msg !== 'Script error.') return;

        const hasPayPalScript = !!document.querySelector('script[src*="paypal.com/sdk/js"]');
        if (!hasPayPalScript) return;

        // Only handle while we're in the PayPal load/render flow
        if (sdkReady) return;

        if (typeof event.preventDefault === 'function') event.preventDefault();
        if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

        setSdkError('PayPal encountered an error. Click "Reload PayPal" and try again.');
      } catch {
        // ignore
      }
    };

    window.addEventListener('error', onErrorCapture, true);
    return () => window.removeEventListener('error', onErrorCapture, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately excludes 'baseOrder' object reference (matches pattern used elsewhere in this file); only its presence/paid-status matters here
  }, [orderIsPaid, sdkReady]);

  // Clear success flags after actions
  useEffect(() => {
    if (
      successPay ||
      successDeliver ||
      successShip ||
      successProcess ||
      successCancelRequest ||
      successCancelDecision ||
      successRefundRequest ||
      successRefundInitiate
    ) {
      dispatch(resetOrderFlags());
    }
  }, [
    dispatch,
    successPay,
    successDeliver,
    successShip,
    successProcess,
    successCancelRequest,
    successCancelDecision,
    successRefundRequest,
    successRefundInitiate,
  ]);

  // Initialize tracking arrays when order loads
  useEffect(() => {
    if (!loading && baseOrder) {
      const serverT = baseOrder.trackingNumbers || [];
      setTrackingNumbers(serverT);
      setBigNumber(toDraftFromExisting(serverT, baseOrder.orderItems));
    }
  }, [loading, baseOrder, toDraftFromExisting]);

  // Update shipment tracking results (only arrays)
  useEffect(() => {
    if (Array.isArray(trackingHistory)) {
      setRes(trackingHistory);
    }
  }, [trackingHistory]);

  // Capture and display admin action errors in modal
  useEffect(() => {
    if (error && (
      orderState.loadingRefundInitiate === false ||
      orderState.loading === false
    )) {
      // Check if this error is from an admin action that just completed
      const isAdminActionError = 
        String(error).includes('cancel') ||
        String(error).includes('refund') ||
        String(error).includes('deliver') ||
        String(error).includes('Cannot') ||
        String(error).includes('already');
      
      if (isAdminActionError) {
        setErrorModalMessage(error);
        setShowErrorModal(true);
        // Clear error after showing
        setTimeout(() => {
          dispatch(resetOrderFlags());
        }, 100);
      }
    }
  }, [error, orderState.loadingRefundInitiate, orderState.loading, dispatch]);

  // Handlers
  const successPaymentHandler = (paymentResult) => {
    const captureId =
      paymentResult?.id ||
      paymentResult?.purchase_units?.[0]?.payments?.captures?.[0]?.id ||
      paymentResult?.purchase_units?.[0]?.payments?.authorizations?.[0]?.id;

    if (!orderId || !captureId) {
      console.error('Missing orderId or PayPal transaction id', { orderId, paymentResult });
      return;
    }

    const paymentPayload = {
      id: captureId,
      status: paymentResult?.status,
      update_time: paymentResult?.update_time,
      email: paymentResult?.payer?.email_address,
    };

    dispatch(payOrder({ orderId, paymentResult: paymentPayload }));
  };

  const processHandler = () => {
    if (!orderId) return;
    dispatch(markOrderProcessed(orderId));
  };

  // Admin refund actions (initiate/approve/deny) are step-up-gated. A
  // pending action is stashed here until the password prompt resolves, then
  // run with the resulting token attached.
  const [pendingStepUpAction, setPendingStepUpAction] = useState(null);
  const [stepUpLoading, setStepUpLoading] = useState(false);
  const [stepUpError, setStepUpError] = useState('');

  const requireStepUpThen = (action) => {
    setStepUpError('');
    setPendingStepUpAction(() => action);
  };

  const handleStepUpConfirm = async (password) => {
    setStepUpLoading(true);
    setStepUpError('');
    try {
      const stepUpToken = await requestStepUpToken(password, userInfo?.token);
      const action = pendingStepUpAction;
      setPendingStepUpAction(null);
      await action(stepUpToken);
    } catch (e) {
      setStepUpError(e.message);
    } finally {
      setStepUpLoading(false);
    }
  };

  const initiateRefundHandler = (message) => {
    if (!orderId) return;
    requireStepUpThen((stepUpToken) => {
      dispatch(initiateRefund({ orderId, message, stepUpToken }));
    });
  };

  const approveCancelHandler = (note) => {
    if (!orderId) return;
    dispatch(decideCancelRequest({ orderId, decision: 'approved', note }));
  };

  const denyCancelHandler = (note) => {
    if (!orderId) return;
    dispatch(decideCancelRequest({ orderId, decision: 'denied', note }));
  };

  // Approve refund request handler
  const approveRefundHandler = (note) => {
    requireStepUpThen(async (stepUpToken) => {
      try {
        setLoadingRefundDecision(true);

        // Calculate refund amount including tax for selected items
        const items = baseOrder.refundRequest.items || [];

        // Calculate tax for refunded items
        let itemsSubtotal = 0;
        items.forEach(item => {
          itemsSubtotal += item.price * item.quantity;
        });

        // Calculate proportional tax
        const orderItemsTotal = baseOrder.orderItems.reduce((sum, item) => sum + (item.price * item.qty), 0);
        const taxRate = orderItemsTotal > 0 ? Number(baseOrder.taxPrice || 0) / orderItemsTotal : 0;
        const refundTax = itemsSubtotal * taxRate;
        const totalRefundAmount = itemsSubtotal + refundTax;

        // Dispatch approve action with refund details
        await dispatch(approveRefundRequest({
          orderId: baseOrder._id,
          refundAmount: totalRefundAmount.toFixed(2),
          items: items,
          adminNote: note || '',
          stepUpToken,
        })).unwrap();

        // Refresh order details
        dispatch(getOrderDetails(orderId));
        setShowApproveRefundModal(false);
        setLoadingRefundDecision(false);
      } catch (err) {
        setLoadingRefundDecision(false);
        setErrorModalMessage(String(err?.message || err || 'Failed to approve refund request.'));
        setShowErrorModal(true);
      }
    });
  };

  // Deny refund request handler
  const denyRefundHandler = (reason) => {
    requireStepUpThen(async (stepUpToken) => {
      try {
        setLoadingRefundDecision(true);

        await dispatch(denyRefundRequest({
          orderId: baseOrder._id,
          reason: reason || 'No reason provided',
          stepUpToken,
        })).unwrap();

        // Refresh order details
        dispatch(getOrderDetails(orderId));
        setShowDenyRefundModal(false);
        setLoadingRefundDecision(false);
      } catch (err) {
        setLoadingRefundDecision(false);
        setErrorModalMessage(String(err?.message || err || 'Failed to deny refund request.'));
        setShowErrorModal(true);
      }
    });
  };

  const adminCancelOrderHandler = (message) => {
    if (!orderId) return;
    dispatch(adminCancelOrder({ orderId, message }));
  };

  const adminDeliverOrderHandler = (message) => {
    if (!orderId) return;
    dispatch(deliverOrder({ orderId, message }));
  };

  const handleHelpCancel = () => {
    setHelpMessage('');
    const cancelled = baseOrder?.status === 'cancelled';
    const processed = Boolean(baseOrder?.isProcessed);
    const shippedOrDelivered = Boolean(baseOrder?.isShipped || baseOrder?.isDelivered);
    const cancelPending = String(baseOrder?.cancellationRequest?.status || '') === 'pending';

    if (cancelled) {
      setHelpMessageVariant('danger');
      setHelpMessage('This order has already been cancelled.');
      return;
    }
    if (processed || shippedOrDelivered) {
      setHelpMessageVariant('warning');
      setHelpMessage('Cancellation is no longer eligible for this order.');
      return;
    }
    if (cancelPending) {
      setHelpMessageVariant('info');
      setHelpMessage('A cancellation request is already pending for this order.');
      return;
    }
    history.push(`/order/${orderId}/cancel`);
  };

  const handleHelpRefund = () => {
    setHelpMessage('');
    const refunded = baseOrder?.status === 'refunded';
    const refundPending = String(baseOrder?.refundRequest?.status || '') === 'pending';
    
    if (refunded) {
      setHelpMessageVariant('warning');
      setHelpMessage('This order has already been refunded.');
      return;
    }
    if (refundPending) {
      setHelpMessageVariant('info');
      setHelpMessage('A refund request is already pending for this order.');
      return;
    }
    if (!baseOrder?.isDelivered) {
      setHelpMessageVariant('warning');
      setHelpMessage('Refund requests are only available after delivery.');
      return;
    }
    history.push(`/order/${orderId}/request-refund`);
  };

  const shipHandler = () => setShowModal(true);
  const handleConfirmClose = () => {
    setShowModal(false);
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    const email = orderEmail;
    // Build structured shipments (number + items with index and qty)
    const shipments = (bigNumber || []).map((t) => ({
      number: t?.number || '',
      items: (t?.items || [])
        .filter((x) => String(x.orderItemIndex || '') !== '' && Number(x.qty) > 0)
        .map((x) => ({ orderItemIndex: String(x.orderItemIndex), qty: Number(x.qty) })),
    }));

    // Legacy labels for compatibility/display
    const toLabel = (val) => orderItemOptions.find((o) => o.value === val)?.label || val;
    const legacy = shipments.map((t) => ({
      number: t.number,
      trackingItem: (t.items || []).map((x) => `${toLabel(x.orderItemIndex)} ×${x.qty}`),
    }));

    const payload = { orderId, email, shipments, bigNumber: legacy };
    dispatch(sendTrackingNumbers(payload));
    // Optimistic bottom list update
    setTrackingNumbers(legacy);
    setShowModal(false);
  };

  // Re-add: open shipment tracking modal and fetch history
  const handleLog = (item) => {
    const trackingId = item.number;
  const destinationCountry = baseOrder?.shippingAddress?.country;
    const language = 'en';
  const zipcode = baseOrder?.shippingAddress?.postalCode;
    dispatch(sendShipmentTracking({ trackingId, destinationCountry, language, zipcode }));
    setShowShipmentModal(true);
  };

  // Tracking number management
  const handleTrackingNumberChange = (index, value) => {
    setBigNumber((prev) => {
      const copy = [...prev];
      if (!copy[index]) return prev;
      copy[index] = { ...copy[index], number: value };
      return copy;
    });
  };
  // New: select change handler for dropdowns
  const handleTrackingSelectChange = (trackingIndex, itemIndex, orderItemIndex) => {
    setBigNumber((prev) => {
      const copy = [...prev];
      if (!copy[trackingIndex]) return prev;
      const row = { ...copy[trackingIndex] };
      const items = Array.isArray(row.items) ? [...row.items] : [];
      const current = { ...(items[itemIndex] || { qty: 1 }) };
      current.orderItemIndex = orderItemIndex;
      // Clamp qty against remaining (excluding this row)
      const idxKey = String(orderItemIndex || '');
      // compute used elsewhere
      let usedElse = 0;
      (copy || []).forEach((ship, sI) => {
        (ship?.items || []).forEach((it, iI) => {
          if (sI === trackingIndex && iI === itemIndex) return;
          if (String(it.orderItemIndex) === idxKey) usedElse += Number(it.qty) || 0;
        });
      });
      const total = Number(orderItemOptions.find((o) => o.value === idxKey)?.qty || 0);
      const maxAllowed = Math.max(0, total - usedElse);
      current.qty = Math.min(Math.max(Number(current.qty) || 1, 1), Math.max(1, maxAllowed));
      items[itemIndex] = current;
      row.items = items;
      copy[trackingIndex] = row;
      return copy;
    });
  };
  const handleTrackingQtyChange = (trackingIndex, itemIndex, qty) => {
    setBigNumber((prev) => {
      const copy = [...prev];
      if (!copy[trackingIndex]) return prev;
      const row = { ...copy[trackingIndex] };
      const items = Array.isArray(row.items) ? [...row.items] : [];
      const current = { ...(items[itemIndex] || {}) };
      const idxKey = String(current.orderItemIndex || '');
      if (!idxKey) return prev;
      // used elsewhere excluding this row
      let usedElse = 0;
      (copy || []).forEach((ship, sI) => {
        (ship?.items || []).forEach((it, iI) => {
          if (sI === trackingIndex && iI === itemIndex) return;
          if (String(it.orderItemIndex) === idxKey) usedElse += Number(it.qty) || 0;
        });
      });
      const total = Number(orderItemOptions.find((o) => o.value === idxKey)?.qty || 0);
      const maxAllowed = Math.max(0, total - usedElse);
      const q = Math.max(1, Math.min(Number(qty) || 1, maxAllowed || 1));
      current.qty = q;
      items[itemIndex] = current;
      row.items = items;
      copy[trackingIndex] = row;
      return copy;
    });
  };
  const addTrackingItem = (index) => {
    setBigNumber((prev) => {
      const copy = [...prev];
      if (!copy[index]) return prev;
      const row = { ...copy[index] };
      row.items = Array.isArray(row.items) ? [...row.items, { orderItemIndex: '', qty: 1 }] : [{ orderItemIndex: '', qty: 1 }];
      copy[index] = row;
      return copy;
    });
  };
  const removeTrackingItem = (trackingIndex, itemIndex) => {
    setBigNumber((prev) => {
      const copy = [...prev];
      if (!copy[trackingIndex]) return prev;
      const row = { ...copy[trackingIndex] };
      const items = Array.isArray(row.items) ? [...row.items] : [];
      items.splice(itemIndex, 1);
      row.items = items;
      copy[trackingIndex] = row;
      return copy;
    });
  };
  const addNewTrackingNumber = () => {
    setBigNumber((prev) => [...prev, { number: '', items: [] }]);
  };
  const removeTrackingNumber = (index) => {
    setBigNumber((prev) => {
      const copy = [...prev];
      copy.splice(index, 1);
      return copy;
    });
  };

  // Guest verification submit: request short-lived access token
  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    setVerifyError('');
    const emailNorm = (verifyEmail || '').trim().toLowerCase();
    const zipNorm = (verifyZip || '').trim();
    if (!emailNorm || !zipNorm) {
      setVerifyError('Please enter email and postal code');
      return;
    }
    try {
  const state = { user: { userInfo } };
  const config = getAxiosConfig(state, { withCredentials: true, forbidAuthForGuest: true });
  const { data } = await axios.post(`/api/orders/${orderId}/guest-access`, {
        email: emailNorm,
        postalCode: zipNorm,
  }, config);
      if (data?.loggedIn) {
        // Guest session cookie is now set; fetch via normal protected/any route
        setVerifyOpen(false);
        setVerifyEmail('');
        setVerifyZip('');
        setVerifyError('');
        // Clear any previous one-off order access token for safety
        sessionStorage.removeItem(accessKey);
        // Re-fetch order via standard path
        await fetchOrder({ force: true });
  // Hydrate frontend user session from server
  await ensureGuestSession();
      } else {
        const token = data?.accessToken || data?.token;
        if (!token) throw new Error('No token returned');
        sessionStorage.setItem(accessKey, token);
        setVerifyOpen(false);
        await fetchOrder({ force: true });
  // Set a minimal guest user session on frontend so UI reflects logged-in guest state
  const guestUi = { username: 'guest', firstName: 'GUEST', lastName: 'GUEST', isGuest: true };
  dispatch(setUserInfo(guestUi));
  try { localStorage.setItem('userInfo', JSON.stringify(guestUi)); } catch {}
      }
    } catch (err) {
      const code = err?.response?.data?.code;
      const msg = err?.response?.data?.message || err?.message || 'Verification failed';
      if (code === 'LOGIN_REQUIRED') {
        try { sessionStorage.removeItem(accessKey); } catch {}
        setVerifyOpen(false);
        setVerifyError('');
        setLoginRequiredMsg(String(msg || 'Please log in to view this order.'));
        setLoginRequiredOpen(true);
        return;
      }
      setVerifyError(msg);
    }
  };

  // After SDK is ready, render PayPal Buttons into the container
  useEffect(() => {
    if (!baseOrder || baseOrder.isPaid) return;
    if (selectedPaymentMethod !== 'PayPal') return;
    const container = paypalRef.current; // capture once
    if (!sdkReady || !isPayPalReady() || !container) return;

    // Clear any previous render
    container.innerHTML = '';

    const amountValue = Number(baseOrder.totalPrice || 0).toFixed(2);

    try {
      const ns = window[currentNsRef.current];
      ns
        .Buttons({
          style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' },
          createOrder: (data, actions) => {
            return actions.order.create({
              purchase_units: [
                {
                  amount: { value: amountValue, currency_code: 'USD' },
                  custom_id: baseOrder._id,
                },
              ],
            });
          },
          onApprove: async (data, actions) => {
            const details = await actions.order.capture();
            successPaymentHandler(details);
          },
          onError: (err) => {
            console.error('[PayPal] onError', err);
            setSdkError('PayPal error. Please reload.');
          },
        })
        .render(container);
    } catch (e) {
      console.error('[PayPal] render failed', e);
      setSdkError('Failed to render PayPal Buttons');
    }

    // Cleanup on unmount or re-render
    return () => {
      try {
        if (container) container.innerHTML = '';
      } catch {
        // ignore
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately depends on baseOrder's stable fields (matches pattern used elsewhere in this file); successPaymentHandler is stable per-render and re-including it would re-render the PayPal buttons unnecessarily
  }, [sdkReady, baseOrder?._id, baseOrder?.isPaid, baseOrder?.totalPrice, selectedPaymentMethod, paypalRenderNonce]);

  const isGuestView = !userInfo;
  // Only block rendering if we have no order to show
  if (!baseOrder) {
    if (isGuestView) {
      if (guestLoading) return <Loader />;
      if (verifyError && !verifyOpen) return <Message variant="danger">{verifyError}</Message>;
      // If verification is required, render the page scaffolding so the modal can appear
    } else {
      if (loading) return <Loader />;
      if (error) return <Message variant="danger">{error}</Message>;
    }
  }

  return (
    <>
      <Meta title='Order — lotustor.com' robots='noindex,follow' />
      {/* Guest verification modal remains available even if baseOrder not yet loaded.
          Non-dismissable (no onHide/close/Cancel): fetchOrder's 401/403 handler opens
          this without setting verifyError, so letting a guest dismiss it would fall
          through to a blank order page with no way back short of a refresh. */}
      <Modal show={verifyOpen} backdrop="static" keyboard={false}>
        <Modal.Header>
          <Modal.Title>Verify Order Access</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Please enter the email and postal code used for shipping to view this order.</p>
          {verifyError && <Message variant="danger">{verifyError}</Message>}
          <Form onSubmit={handleVerifySubmit}>
            <Form.Group controlId="verifyEmail" className="mb-3">
              <Form.Label>Email used for shipping</Form.Label>
              <Form.Control
                type="email"
                value={verifyEmail}
                onChange={(e) => setVerifyEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
            </Form.Group>
            <Form.Group controlId="verifyZip" className="mb-3">
              <Form.Label>Postal code on order</Form.Label>
              <Form.Control
                type="text"
                value={verifyZip}
                onChange={(e) => setVerifyZip(e.target.value)}
                placeholder="ZIP / Postal Code"
                required
              />
            </Form.Group>
            <div className="d-flex justify-content-end">
              <Button type="submit" variant="primary">
                Verify
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Login required modal for registered-user orders */}
      <Modal show={loginRequiredOpen} onHide={() => setLoginRequiredOpen(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Login Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message variant="warning">{loginRequiredMsg || 'Please log in to view this order.'}</Message>
          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={() => setLoginRequiredOpen(false)}>
              Close
            </Button>
            <Link
              className="btn btn-primary"
              to={`/login?redirect=order/${orderId}`}
              onClick={() => setLoginRequiredOpen(false)}
            >
              Log In
            </Link>
          </div>
        </Modal.Body>
      </Modal>
      <div className={showPayNowFab ? 'pb-5 pb-md-0' : undefined}>
        <h1 className="text-break" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
          Order {baseOrder?._id}
        </h1>

        {/* Order Status Banner */}
        {(() => {
          // Determine display status (from status field or derived from flags for legacy orders)
          const status = baseOrder?.status || 
            (baseOrder?.cancellationRequest?.status === 'approved' ? 'cancelled' : null) ||
            (baseOrder?.refundRequest?.status === 'refunded' ? 'refunded' : null) ||
            (baseOrder?.isDelivered ? 'delivered' : null) ||
            (baseOrder?.isShipped ? 'shipped' : null) ||
            (baseOrder?.isProcessed ? 'processing' : null);
          
          if (!status || status === 'pending') return null;
          
          return (
            <Alert
              variant={
                status === 'cancelled'
                  ? 'danger'
                  : status === 'refunded'
                  ? 'info'
                  : status === 'delivered'
                  ? 'success'
                  : status === 'shipped'
                  ? 'info'
                  : 'secondary'
              }
              className='mb-3'
            >
              <strong>Order Status:</strong>{' '}
              <span style={{ textTransform: 'capitalize' }}>{status}</span>
              {status === 'cancelled' && baseOrder.cancellationRequest?.decisionNote && (
                <div className='mt-2'>
                  <small>
                    <strong>Admin Note:</strong> {baseOrder.cancellationRequest.decisionNote}
                  </small>
                </div>
              )}
              {status === 'refunded' && baseOrder.refund?.refundId && (
                <div className='mt-2'>
                  <small>
                    <strong>Refund ID:</strong> {baseOrder.refund.refundId}
                  </small>
                </div>
              )}
            </Alert>
          );
        })()}

        {!isAdmin && orderLoaded && (
          <div className="mb-3">
            {helpMessage && <Message variant={helpMessageVariant}>{helpMessage}</Message>}
            <Dropdown>
              <Dropdown.Toggle variant="secondary" id="order-help-dropdown">
                Help
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={handleHelpCancel}>Cancel My Order</Dropdown.Item>
                <Dropdown.Item as={Link} to="/contactSupport">
                  Help With This Order
                </Dropdown.Item>
                <Dropdown.Item onClick={handleHelpRefund}>Request Refund</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}

        <Row>
          <Col md={8}>
            <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>Shipping</h2>
              <p>
                <strong>Name:</strong> {orderFirstName} {orderLastName}
              </p>
              <p>
                <strong>Email: </strong>{' '}
                {orderEmail && <a href={`mailto:${orderEmail}`}>{orderEmail}</a>}
              </p>
              {baseOrder?.shippingAddress && (
                <p>
                  <strong>Address:</strong> {buildAddressLine(baseOrder.shippingAddress)}
                </p>
              )}
              {baseOrder?.isShipped ? (
                <Message variant="success">
                  Shipped on {new Date(baseOrder.shippedAt).toLocaleString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </Message>
              ) : (
                <Message variant="danger">Not Shipped</Message>
              )}
              {baseOrder?.isDelivered ? (
                <Message variant="success">
                  Delivered on {new Date(baseOrder.deliveredAt).toLocaleString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </Message>
              ) : (
                <Message variant="danger">Not Delivered</Message>
              )}

              {baseOrder?.isProcessed ? (
                <Message variant="success">Processed</Message>
              ) : (
                <Message variant="warning">Not Processed</Message>
              )}
            </ListGroup.Item>

            <ListGroup.Item>
              <h2>Payment Method</h2>
              <p>
                <strong>Method: </strong>
                {orderIsPaid ? baseOrder?.paymentMethod : selectedPaymentMethod}
              </p>
              {orderIsPaid ? (
                <>
                  <Message variant="success">
                    Paid on {new Date(baseOrder.paidAt).toLocaleString('en-US', {
                      month: '2-digit',
                      day: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </Message>
                  {baseOrder?.paymentResult?.id && (
                    <div className="mt-2">
                      <small className="text-muted">
                        Txn: {baseOrder.paymentResult.id} • Status: {baseOrder.paymentResult.status}
                        {baseOrder.paymentResult.email_address ? ` • Payer: ${baseOrder.paymentResult.email_address}` : ''}
                      </small>
                    </div>
                  )}
                </>
              ) : (
                <Message variant="danger">Not Paid</Message>
              )}
            </ListGroup.Item>

            <ListGroup.Item>
              <h2>Order Items</h2>
              {Array.isArray(baseOrder?.orderItems) ? (
                baseOrder.orderItems.length === 0 ? (
                  <Message>Order is empty</Message>
                ) : (
                  <ListGroup variant="flush">
                    {baseOrder.orderItems.map((item, index) => (
                      <ListGroup.Item key={index}>
                        <Row>
                          <Col xs="auto" className="pe-0">
                            <Image
                              src={normalizeImageUrl(item.image)}
                              alt={item.name}
                              rounded
                              style={{ width: 80, height: 80, objectFit: 'cover' }}
                            />
                          </Col>
                          <Col>
                            <Link to={`/product/${item.product}`}>{item.name}</Link>
                          </Col>
                          <Col>
                            {getVariantLabel(item) ? (
                              <span>Color/Variation: {getVariantLabel(item)}</span>
                            ) : (
                              <span>Color/Variation: -</span>
                            )}
                          </Col>
                          <Col md={4}>
                            {item.qty} x {formatMoney(item.price)} = {formatMoney(item.qty * item.price)}
                          </Col>
                        </Row>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )
              ) : (
                <Message>Loading order or no order found</Message>
              )}
            </ListGroup.Item>
            </ListGroup>
          </Col>

          <Col md={4}>
            <Card>
            <ListGroup variant="flush">
              <ListGroup.Item>
                <h2>Order Summary</h2>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col>Items</Col>
                  <Col>{formatMoney(itemsSubtotal)}</Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col>Shipping</Col>
                  <Col>{formatMoney(baseOrder?.shippingPrice)}</Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col>Tax</Col>
                  <Col>{formatMoney(baseOrder?.taxPrice)}</Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col>Total</Col>
                  <Col>{formatMoney(baseOrder?.totalPrice)}</Col>
                </Row>
              </ListGroup.Item>

              {!orderIsPaid && (
                <ListGroup.Item ref={paymentSectionRef} data-testid="order-payment-section">
                  <h2>Payment</h2>
                  <Form>
                    <Form.Check
                      type="radio"
                      label="PayPal or Credit Card"
                      id="orderPayPal"
                      name="orderPaymentMethod"
                      value="PayPal"
                      checked={selectedPaymentMethod === 'PayPal'}
                      onChange={(e) => {
                        paymentMethodTouchedRef.current = true;
                        const next = e.target.value;
                        setSelectedPaymentMethod(next);
                        // Do not tear down the PayPal SDK when switching methods;
                        // it can cause cross-origin "Script error." crashes in dev.
                        if (next === 'PayPal') setSdkError(null);
                      }}
                    />
                    <Form.Check
                      type="radio"
                      label="Credit/Debit Card (Stripe)"
                      id="orderStripe"
                      name="orderPaymentMethod"
                      value="Stripe"
                      checked={selectedPaymentMethod === 'Stripe'}
                      onChange={(e) => {
                        paymentMethodTouchedRef.current = true;
                        const next = e.target.value;
                        setSelectedPaymentMethod(next);
                        // Keep PayPal SDK loaded to avoid cross-origin "Script error." issues.
                        // The PayPal UI section will unmount while Stripe is selected.
                      }}
                    />

                    {selectedPaymentMethod === 'PayPal' ? (
                      <div data-testid="order-payment-ui-paypal" className="mt-2" />
                    ) : (
                      <div data-testid="order-payment-ui-stripe" className="mt-2" />
                    )}
                  </Form>
                </ListGroup.Item>
              )}

              {/* Non-Admin: Show pending cancellation/refund requests */}
              {!isAdmin && String(baseOrder?.cancellationRequest?.status || '') === 'pending' && (
                <ListGroup.Item>
                  <Message variant="info">
                    <strong>Cancellation Request Pending</strong>
                    <div className="mt-2">
                      Your cancellation request is being reviewed by our team.
                    </div>
                  </Message>
                </ListGroup.Item>
              )}

              {!isAdmin && String(baseOrder?.refundRequest?.status || '') === 'pending' && (
                <ListGroup.Item>
                  <Message variant="info">
                    <strong>Refund Request Pending</strong>
                    <div className="mt-2">
                      Your refund request is being reviewed by our team.
                    </div>
                  </Message>
                </ListGroup.Item>
              )}

              {!isAdmin && String(baseOrder?.refundRequest?.status || '') === 'denied' && (
                <ListGroup.Item>
                  <Message variant="danger">
                    <strong>Refund Request Denied</strong>
                    <div className="mt-2">
                      We were unable to approve your refund request for this order.
                    </div>
                    {baseOrder.refundRequest.decisionNote && (
                      <div className="mt-2">
                        <small>
                          <strong>Reason:</strong> {baseOrder.refundRequest.decisionNote}
                        </small>
                      </div>
                    )}
                    <div className="mt-2">
                      <small className="text-muted">
                        Decided: {formatRefundSubmittedAt(baseOrder.refundRequest.decidedAt)}
                      </small>
                    </div>
                  </Message>
                </ListGroup.Item>
              )}

              {/* Show successful refund confirmation to all users */}
              {baseOrder?.refund?.status === 'succeeded' && (
                <ListGroup.Item>
                  <Message variant="success">
                    <strong>✓ Refund Processed Successfully</strong>
                    <div className="mt-2">
                      <small>
                        <strong>Provider:</strong> {baseOrder.refund.provider === 'stripe' ? 'Stripe' : 'PayPal'}<br />
                        <strong>Refund ID:</strong> {baseOrder.refund.refundId}<br />
                        {baseOrder.refund.amount && (
                          <>
                            <strong>Amount:</strong> ${Number(baseOrder.refund.amount).toFixed(2)}<br />
                          </>
                        )}
                        <strong>Processed:</strong> {new Date(baseOrder.refund.initiatedAt).toLocaleString()}
                      </small>
                    </div>
                  </Message>
                </ListGroup.Item>
              )}

              {/* Admin: Show pending/failed refund status */}
              {isAdmin && baseOrder?.refund?.status === 'pending' && (
                <ListGroup.Item>
                  <Message variant="warning">
                    <strong>⏳ Refund Processing</strong>
                    <div className="mt-2">
                      <small>
                        The refund is being processed by {baseOrder.refund.provider === 'stripe' ? 'Stripe' : 'PayPal'}.
                      </small>
                    </div>
                  </Message>
                </ListGroup.Item>
              )}

              {isAdmin && baseOrder?.refund?.status === 'failed' && (
                <ListGroup.Item>
                  <Message variant="danger">
                    <strong>✗ Refund Failed</strong>
                    <div className="mt-2">
                      <small>
                        The refund could not be processed. Please check the payment provider dashboard or try again.
                      </small>
                    </div>
                  </Message>
                </ListGroup.Item>
              )}

              {/* Admin Buttons */}
              {loadingProcess && <Loader />}
              {isAdmin && !baseOrder?.isProcessed && baseOrder?.status !== 'cancelled' && (
                <ListGroup.Item>
                  <Button type="button" className="btn btn-block" onClick={processHandler} disabled={loadingProcess}>
                    Mark As Processed
                  </Button>
                </ListGroup.Item>
              )}

              {loadingCancelDecision && <Loader />}
              {isAdmin && String(baseOrder?.cancellationRequest?.status || '') === 'pending' && (
                <ListGroup.Item>
                  <Message variant="warning">Cancellation request pending</Message>
                  <div className="d-flex gap-2">
                    <Button 
                      type="button" 
                      variant="success" 
                      onClick={() => setShowApproveModal(true)} 
                      disabled={loadingCancelDecision}
                    >
                      Approve Cancellation
                    </Button>
                    <Button 
                      type="button" 
                      variant="danger" 
                      onClick={() => setShowDenyModal(true)} 
                      disabled={loadingCancelDecision}
                    >
                      Deny Cancellation
                    </Button>
                  </div>
                </ListGroup.Item>
              )}

              {/* Admin: Customer Refund Request Notification */}
              {loadingRefundDecision && <Loader />}
              {isAdmin && String(baseOrder?.refundRequest?.status || '') === 'pending' && (
                <ListGroup.Item>
                  <Message variant="warning">
                    <strong>⚠️ Customer Refund Request Pending</strong>
                    <div className="mt-2">
                      <small>
                        <strong>Reason:</strong> {baseOrder.refundRequest.reason || 'Not specified'}
                      </small>
                    </div>
                    {baseOrder.refundRequest.description && (
                      <div className="mt-1">
                        <small>
                          <strong>Details:</strong> {baseOrder.refundRequest.description}
                        </small>
                      </div>
                    )}
                    {baseOrder.refundRequest.items && baseOrder.refundRequest.items.length > 0 && (
                      <div className="mt-2">
                        <small>
                          <strong>Items requested for refund:</strong>
                          <ul className="mb-0 mt-1">
                            {baseOrder.refundRequest.items.map((item, idx) => (
                              <li key={idx}>
                                {item.name} × {item.quantity} (${(item.price * item.quantity).toFixed(2)})
                              </li>
                            ))}
                          </ul>
                        </small>
                      </div>
                    )}
                    {baseOrder.refundRequest.refundAmount && (
                      <div className="mt-2">
                        <strong>Requested Refund Amount: ${Number(baseOrder.refundRequest.refundAmount).toFixed(2)}</strong>
                        {(() => {
                          // Calculate tax for display
                          const items = baseOrder.refundRequest.items || [];
                          let itemsSubtotal = 0;
                          items.forEach(item => {
                            itemsSubtotal += item.price * item.quantity;
                          });
                          const orderItemsTotal = baseOrder.orderItems.reduce((sum, item) => sum + (item.price * item.qty), 0);
                          const taxRate = orderItemsTotal > 0 ? Number(baseOrder.taxPrice || 0) / orderItemsTotal : 0;
                          const refundTax = itemsSubtotal * taxRate;
                          const totalWithTax = itemsSubtotal + refundTax;
                          
                          return refundTax > 0 ? (
                            <div className="mt-1">
                              <small className="text-muted">
                                (+ ${refundTax.toFixed(2)} tax = <strong>${totalWithTax.toFixed(2)} total refund</strong>)
                              </small>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    )}
                    <div className="mt-2">
                      <small className="text-muted">
                        Submitted: {formatRefundSubmittedAt(baseOrder.refundRequest.submittedAt)}
                      </small>
                    </div>
                  </Message>
                  
                  <div className="d-flex gap-2 mt-2">
                    <Button 
                      type="button" 
                      variant="success" 
                      onClick={() => setShowApproveRefundModal(true)} 
                      disabled={loadingRefundDecision}
                    >
                      Approve & Issue Refund
                    </Button>
                    <Button 
                      type="button" 
                      variant="danger" 
                      onClick={() => setShowDenyRefundModal(true)} 
                      disabled={loadingRefundDecision}
                    >
                      Deny Refund
                    </Button>
                  </div>
                </ListGroup.Item>
              )}

              {isAdmin && String(baseOrder?.refundRequest?.status || '') === 'denied' && (
                <ListGroup.Item>
                  <Message variant="secondary">
                    <strong>Customer Refund Request Denied</strong>
                    <div className="mt-2">
                      <small>
                        <strong>Reason:</strong> {baseOrder.refundRequest.reason || 'Not specified'}
                      </small>
                    </div>
                    {baseOrder.refundRequest.decisionNote && (
                      <div className="mt-1">
                        <small>
                          <strong>Denial note sent to customer:</strong> {baseOrder.refundRequest.decisionNote}
                        </small>
                      </div>
                    )}
                    <div className="mt-2">
                      <small className="text-muted">
                        Decided: {formatRefundSubmittedAt(baseOrder.refundRequest.decidedAt)}
                      </small>
                    </div>
                  </Message>
                </ListGroup.Item>
              )}

              {loadingRefundInitiate && <Loader />}
              {isAdmin && (
                <ListGroup.Item>
                  <Button
                    type="button"
                    className="btn btn-block"
                    onClick={() => setShowAdminRefundModal(true)}
                    disabled={loadingRefundInitiate}
                  >
                    Initiate Refund
                  </Button>
                </ListGroup.Item>
              )}

              {loadingShip && <Loader />}
              {orderIsPaid && !baseOrder?.isShipped && isAdmin && (
                <ListGroup.Item>
                  <Button type="button" className="btn btn-block" onClick={shipHandler}>
                    Mark As Shipped
                  </Button>
                </ListGroup.Item>
              )}

              {loadingDeliver && <Loader />}
              {orderIsPaid && !baseOrder?.isDelivered && isAdmin && (
                <ListGroup.Item>
                  <Button type="button" className="btn btn-block" onClick={() => setShowAdminDeliverModal(true)}>
                    Mark As Delivered
                  </Button>
                </ListGroup.Item>
              )}

              {/* Admin Cancel Order Button */}
              {isAdmin && baseOrder?.status !== 'cancelled' && (
                <ListGroup.Item>
                  <Button type="button" className="btn btn-block btn-danger" onClick={() => setShowAdminCancelModal(true)}>
                    Cancel Order
                  </Button>
                </ListGroup.Item>
              )}

              {/* Payment */}
              {!orderIsPaid && selectedPaymentMethod === 'PayPal' && (
                <ListGroup.Item>
                  <div data-testid="order-payment-paypal-container" />
                  {loadingPay && <Loader />}
                  {sdkError && <Message variant="danger">{sdkError}</Message>}
                  {!sdkReady && !sdkError ? (
                    <>
                      <Loader />
                      <div className="mt-2 d-flex align-items-center">
                        <Button variant="secondary" size="sm" onClick={handleReloadPayPal}>
                          Reload PayPal
                        </Button>
                        {sdkAttempts > 0 && (
                          <small className="text-muted ms-2">Attempt {sdkAttempts}/{MAX_SDK_ATTEMPTS}</small>
                        )}
                      </div>
                    </>
                  ) : !sdkError ? (
                    <>
                      <div id="paypal-buttons-container" ref={paypalRef} />
                      <div className="mt-2">
                        <Button variant="secondary" size="sm" onClick={handleReloadPayPal}>
                          Reload PayPal
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="mt-2">
                      <Button variant="secondary" size="sm" onClick={handleReloadPayPal}>
                        Reload PayPal
                      </Button>
                    </div>
                  )}
                </ListGroup.Item>
              )}

              {!orderIsPaid && selectedPaymentMethod === 'Stripe' && (
                <ListGroup.Item>
                  <div data-testid="order-payment-stripe-container" />
                  {loadingPay && <Loader />}
                  {stripeKeyError && <Message variant="danger">{stripeKeyError}</Message>}
                  {!stripePromise && !stripeKeyError ? (
                    <Loader />
                  ) : !stripeKeyError ? (
                    <StripePayForm orderId={orderId} stripePromise={stripePromise} />
                  ) : null}
                </ListGroup.Item>
              )}
            </ListGroup>
            </Card>
          </Col>
        </Row>
      </div>

      {showPayNowFab && (
        <Button
          variant="primary"
          className="d-md-none"
          data-testid="order-paynow-fab"
          aria-label="Pay Now"
          onClick={scrollToPayment}
          style={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 1050,
            padding: '12px 24px',
            fontSize: '1rem',
            fontWeight: 600,
            borderRadius: 50,
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          }}
        >
          Pay Now
        </Button>
      )}

      {/* Upload Tracking Numbers Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Upload Tracking Numbers</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            {(bigNumber || []).map((tracking, trackingIndex) => (
              <div key={trackingIndex}>
                <Form.Group controlId={`trackingNumber-${trackingIndex}`}>
                  <Form.Label>Tracking Number</Form.Label>
                  <Row>
                    <Col>
                      <Form.Control
                        type="text"
                        value={tracking.number}
                        onChange={(e) => handleTrackingNumberChange(trackingIndex, e.target.value)}
                      />
                    </Col>
                    <Col md="auto">
                      <Button variant="danger" onClick={() => removeTrackingNumber(trackingIndex)}>
                        Remove
                      </Button>
                    </Col>
                  </Row>
                </Form.Group>

                {(tracking.items || []).map((it, itemIndex) => {
                  const idx = String(it?.orderItemIndex || '');
                  // Available: items with remaining > 0, or the currently selected one
                  const available = orderItemOptions.filter((opt) => {
                    if (opt.value === idx) return true;
                    const rem = Number(remainingByIndex[opt.value] || 0);
                    return rem > 0;
                  });
                  const currentOpt = optionWithRemaining(idx);
                  const currentQty = Number(it?.qty) || 1;
                  const maxForCurrent = (() => {
                    if (!idx) return 1;
                    // remaining excluding this row = remainingByIndex[idx] + currentQty
                    const rem = Number(remainingByIndex[idx] || 0);
                    return Math.max(1, rem + currentQty);
                  })();
                  return (
                    <Form.Group key={itemIndex} controlId={`trackingItem-${trackingIndex}-${itemIndex}`}>
                      <Form.Label>Item {itemIndex + 1}</Form.Label>
                      <Row className="align-items-center">
                        <Col>
                          <Form.Control
                            as="select"
                            value={idx}
                            onChange={(e) => handleTrackingSelectChange(trackingIndex, itemIndex, e.target.value)}
                          >
                            <option value="">Select item…</option>
                            {available.map((opt) => {
                              const rem = Number(remainingByIndex[opt.value] || 0);
                              const total = Number(opt.qty || 0);
                              const text = `${opt.label} — remaining ${opt.value === idx ? (rem + currentQty) : rem} of ${total}`;
                              return (
                                <option key={opt.value} value={opt.value}>
                                  {text}
                                </option>
                              );
                            })}
                          </Form.Control>
                        </Col>
                        <Col md="auto">
                          <Form.Control
                            type="number"
                            min={1}
                            max={maxForCurrent}
                            value={currentQty}
                            onChange={(e) => handleTrackingQtyChange(trackingIndex, itemIndex, e.target.value)}
                            style={{ width: 100 }}
                          />
                        </Col>
                        <Col md="auto">
                          <Button variant="danger" onClick={() => removeTrackingItem(trackingIndex, itemIndex)}>
                            Remove Item
                          </Button>
                        </Col>
                      </Row>
                      {idx && currentOpt && currentOpt.remaining === 0 && currentQty >= currentOpt.total && (
                        <small className="text-muted">Full quantity allocated for this item.</small>
                      )}
                    </Form.Group>
                  );
                })}

                <Button
                  variant="secondary"
                  onClick={() => addTrackingItem(trackingIndex)}
                  className="mt-2"
                  disabled={orderItemOptions.filter((opt) => Number(remainingByIndex[opt.value] || 0) > 0).length === 0}
                >
                  Add Item
                </Button>
              </div>
            ))}

            <Button variant="secondary" onClick={addNewTrackingNumber} className="mt-3">
              Add Another Tracking Number
            </Button>

            <div className="mt-3">
              <Button variant="primary" type="submit">
                Submit Tracking Numbers
              </Button>
              <Button variant="secondary" onClick={handleConfirmClose} className="ms-2">
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Shipment Tracking History Modal */}
      <Modal show={showShipmentModal} onHide={() => setShowShipmentModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Shipment Tracking History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errorTracking ? (
            <Message variant="danger">{errorTracking}</Message>
          ) : trackingStatus === 'processing' ? (
            <div className="d-flex align-items-center">
              <Loader />
              <span className="ms-2">Still checking with the carrier…</span>
            </div>
          ) : trackingStatus === 'timed_out' ? (
            <Message variant="warning">
              The carrier hasn't responded yet. Please check again in a moment.
            </Message>
          ) : loadingTracking ? (
            <Loader />
          ) : Array.isArray(res) && res.length ? (
            <div>
              <h2>Movement</h2>
              <ul>
                {res.map((state, index) => (
                  <li key={index}>
                    <p>{state}</p>
                    <br />
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div>No movement data yet</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowShipmentModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Tracking Numbers List */}
      {trackingNumbers.length > 0 && (
        <div className="list-group list-group-flush">
          <h2>Tracking Numbers</h2>
          {trackingNumbers.map((trackingNumber, trackingIndex) => (
            <div key={trackingIndex} className="list-group-item mb-3">
              <Card>
                <ListGroup.Item>
                  <h5>Tracking Number: {trackingNumber.number}</h5>
                </ListGroup.Item>
                <ListGroup>
                  {Array.isArray(trackingNumber.items) && trackingNumber.items.length > 0 ? (
                    trackingNumber.items.map((x, itemIndex) => {
                      const idx = String(x?.orderItemIndex || '');
                      const qty = Number(x?.qty) || 0;
                      const opt = orderItemOptions.find((o) => o.value === idx);
                      const label = opt ? `${opt.name}${opt.variant ? ` (${opt.variant})` : ''}` : `Item ${idx}`;
                      return (
                        <ListGroup.Item key={itemIndex}>
                          <h6>Shipment Item: {label} ×{qty}</h6>
                        </ListGroup.Item>
                      );
                    })
                  ) : (
                    (trackingNumber.trackingItem || []).map((item, itemIndex) => (
                      <ListGroup.Item key={itemIndex}>
                        <h6>Shipment Item: {item}</h6>
                      </ListGroup.Item>
                    ))
                  )}
                </ListGroup>
                <Button variant="primary" onClick={() => handleLog(trackingNumber)} className="mt-2">
                  TRACK SHIPMENT
                </Button>
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* Admin Decision Modals */}
      <AdminDecisionModal
        show={showApproveModal}
        onHide={() => setShowApproveModal(false)}
        onConfirm={approveCancelHandler}
        title="Approve Cancellation"
        actionLabel="Approve & Notify Customer"
        variant="success"
        message="You are about to approve this cancellation request. The customer will receive an email notification with your note."
      />

      <AdminDecisionModal
        show={showDenyModal}
        onHide={() => setShowDenyModal(false)}
        onConfirm={denyCancelHandler}
        title="Deny Cancellation"
        actionLabel="Deny & Notify Customer"
        variant="danger"
        message="You are about to deny this cancellation request. Please provide a clear reason that will be sent to the customer."
      />

      {/* Admin Cancel Order Modal */}
      <AdminDecisionModal
        show={showAdminCancelModal}
        onHide={() => setShowAdminCancelModal(false)}
        onConfirm={adminCancelOrderHandler}
        title="Cancel Order"
        actionLabel="Cancel Order"
        variant="danger"
        message="This will immediately cancel the order and notify the customer. Please provide a reason (optional):"
      />

      {/* Admin Mark as Delivered Modal */}
      <AdminDecisionModal
        show={showAdminDeliverModal}
        onHide={() => setShowAdminDeliverModal(false)}
        onConfirm={adminDeliverOrderHandler}
        title="Mark Order as Delivered"
        actionLabel="Mark as Delivered"
        variant="success"
        message="Add an optional message for the customer about this delivery:"
      />

      {/* Admin Action Error Modal */}
      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Action Failed</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message variant="danger">{errorModalMessage}</Message>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
            Dismiss
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Admin Refund Request Decision Modals */}
      <AdminDecisionModal
        show={showApproveRefundModal}
        onHide={() => setShowApproveRefundModal(false)}
        onConfirm={approveRefundHandler}
        title="Approve Refund Request"
        actionLabel="Approve & Issue Refund"
        variant="success"
        message="You are about to approve this refund request and issue the refund including calculated tax. The customer will receive an email notification. You can optionally include a note:"
      />

      <AdminDecisionModal
        show={showDenyRefundModal}
        onHide={() => setShowDenyRefundModal(false)}
        onConfirm={denyRefundHandler}
        title="Deny Refund Request"
        actionLabel="Deny & Notify Customer"
        variant="danger"
        message="You are about to deny this refund request. Please provide a clear reason that will be sent to the customer via email:"
      />

      {/* Admin Initiate Refund Modal */}
      <AdminDecisionModal
        show={showAdminRefundModal}
        onHide={() => setShowAdminRefundModal(false)}
        onConfirm={initiateRefundHandler}
        title="Initiate Refund"
        actionLabel="Initiate Refund"
        variant="warning"
        message="Are you sure you want to initiate a refund for this order? You can optionally include a message that will be sent to the customer in the refund email:"
      />

      <StepUpPasswordModal
        show={pendingStepUpAction !== null}
        title="Confirm refund action"
        description="For your security, please re-enter your password to proceed with this refund action."
        onCancel={() => setPendingStepUpAction(null)}
        onConfirm={handleStepUpConfirm}
        loading={stepUpLoading}
        error={stepUpError}
      />
    </>
  );
};

export default OrderScreen;
